import { useTimeline } from '@src/features/absence';
import {
  doesTimedRangeOverlapAbsences,
  getUserAbsences,
} from '@src/features/absence/lib';
import { useFormContext } from '@src/shared/form';
import { TimeSelectField } from '@src/shared/ui/fields';
import type { AutocompleteOption } from '@src/shared/ui/inputs';
import { Stack } from '@src/shared/ui/layout';
import { type ComponentPropsWithoutRef, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { ScheduleFormValues } from '../Form';

const StyleSheet = {
  field: {
    '&.MuiAutocomplete-root .MuiInputBase-root.MuiAutocomplete-inputRoot': {
      paddingRight: '50px',
    },
  },
};
type TimeSelectFieldProps = ComponentPropsWithoutRef<typeof TimeSelectField>;

function TimeFromField(props: TimeSelectFieldProps) {
  return (
    <TimeSelectField
      label="From"
      name="startTime"
      sx={StyleSheet.field}
      {...props}
    />
  );
}
function TimeToField(props: TimeSelectFieldProps) {
  return (
    <TimeSelectField
      label="To"
      name="endTime"
      sx={StyleSheet.field}
      {...props}
    />
  );
}

type TimeRangeFieldProps = {
  disabled?: boolean;
  /** Hard bounds (withdrawal): the range may only be narrowed within these. */
  minTime?: string;
  maxTime?: string;
};

export default function TimeRangeField({
  disabled = false,
  minTime,
  maxTime,
}: TimeRangeFieldProps) {
  const { watch } = useFormContext() as UseFormReturn<ScheduleFormValues>;
  const { absences, selectedSlot } = useTimeline();
  const [userId, dateFrom, startTime, endTime] = watch([
    'userId',
    'dateFrom',
    'startTime',
    'endTime',
  ]);

  const userAbsences = useMemo(
    () => getUserAbsences(userId ?? null, absences),
    [absences, userId],
  );

  const excludeIds = useMemo(
    () => (selectedSlot?.editedAbsenceId ? [selectedSlot.editedAbsenceId] : []),
    [selectedSlot?.editedAbsenceId],
  );

  const isOutOfBounds = (value: string) =>
    (!!minTime && value < minTime) || (!!maxTime && value > maxTime);

  const isOccupied = (
    nextStartTime: string | null,
    nextEndTime: string | null,
  ) => {
    if (!dateFrom || !userId || !nextStartTime || !nextEndTime) {
      return false;
    }

    return doesTimedRangeOverlapAbsences(
      userAbsences,
      dateFrom,
      nextStartTime,
      nextEndTime,
      excludeIds,
    );
  };

  return (
    <Stack spacing={2} direction="row">
      <TimeFromField
        disabled={disabled}
        minutesStep={15}
        required
        getOptionDisabled={(option: AutocompleteOption) => {
          const value = String(option.value);

          return (
            isOutOfBounds(value) ||
            (!!endTime && value >= endTime) ||
            isOccupied(value, endTime ?? null)
          );
        }}
      />
      <TimeToField
        disabled={disabled}
        minutesStep={15}
        required
        getOptionDisabled={(option: AutocompleteOption) => {
          const value = String(option.value);

          return (
            isOutOfBounds(value) ||
            (!!startTime && value <= startTime) ||
            isOccupied(startTime ?? null, value)
          );
        }}
      />
    </Stack>
  );
}
